const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

/**
 * 401レスポンス時に AuthContext へ通知するコールバック
 */
let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(callback: (() => void) | null) {
  onUnauthorized = callback;
}

/**
 * XSRF-TOKEN を Cookie から取得する
 */
function getXsrfToken(): string | undefined {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
}

/**
 * API通信の共通クライアント
 * credentials: 'include' で Cookie（セッション）を自動送信する
 * POST/PUT/DELETE リクエストでは X-XSRF-TOKEN ヘッダーを送信する
 */
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const xsrfToken = getXsrfToken();
  const xsrfHeader: Record<string, string> = xsrfToken
    ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken) }
    : {};

  const { headers: optionHeaders, ...restOptions } = options;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...restOptions,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...xsrfHeader,
      ...(optionHeaders as Record<string, string>),
    },
  });

  if (!response.ok) {
    // 401 Unauthenticated: AuthContext に通知して認証状態をリセット
    if (response.status === 401 && !endpoint.includes('/login')) {
      onUnauthorized?.();
      throw { status: 401, data: null } as ApiError;
    }

    const error: ApiError = {
      status: response.status,
      data: await response.json().catch(() => null),
    };
    throw error;
  }

  // DELETEレスポンス等、本文が空の場合
  const text = await response.text();
  if (!text) return null as T;

  return JSON.parse(text) as T;
}

export interface ApiError {
  status: number;
  data: Record<string, unknown> | null;
}

export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export const apiClient = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),
};
